import {
  DASHBOARD_GRAPH_AUCTIONS,
  DashboardGraphID,
  IDashboardHomeCardMetricKey,
} from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import {
  formatSelectedByJoiningIDsWithCommas,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import {
  IListViewTableColumns,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { TimeRange } from '~/src/dash/utils/dateTimeRanges';
import {
  bidErrorsMetrics,
  clearedActiveFilters,
  clientBidErrorsMetrics,
  clientKeyLatencyMetrics,
  DashboardLiveCardGroup,
  IActiveFilters,
  IDashboardSubChart,
  keyLatencyMetrics,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';

export interface IDashboardSubTable {
  columns: IListViewTableColumns;
}

export interface IDashboardTopDemandSourcesTable extends IDashboardSubTable {
  activeMetricKeys: string[];
}

export interface IDashboardPublicaAnalyticsCardsConfig {
  groupings: Record<DashboardLiveCardGroup, string[]>;
  metrics: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
}

export interface IDashboardPublicaAnalyticsConfig {
  defaultState: {
    activeFilters: IActiveFilters;
    timeRange: TimeRange;
    timezone?: string;
    displayedMetricKeys: IDashboardHomeCardMetricKey[];
    activeChartMetrics: IDashboardChartMetric[];
    isPeriodMenuOpen: boolean;
    isFilterMenuOpen: boolean;
    tableRowsPerPage: RowsPerPage;
    graphID: DashboardGraphID;
    isDemandSourceUsingBidderType: boolean;
  };
  orderedFilterKeys: string[];
  filters: ICustomAPIFilterConfig;
  cards: IDashboardPublicaAnalyticsCardsConfig;
  subCharts: IDashboardSubChart[];
  chartSyncID: string;
}

// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ
export const dashboardPublicaAnalyticsConfig: IDashboardPublicaAnalyticsConfig = {
  chartSyncID: 'dashboard_publica_analytics',
  defaultState: {
    activeFilters: clearedActiveFilters,
    timeRange: '1 Hour By Minute',
    isPeriodMenuOpen: true,
    isFilterMenuOpen: true,
    displayedMetricKeys: [
      'Revenue',
      'WinningBids',
      'Impressions',
      'CPM',
      'EndpointRequests',
      'RenderRate',
    ],
    activeChartMetrics: [
      {
        key: 'Revenue',
        label: 'Revenue',
        chartColorIndex: 0,
        chartType: 'bar',
        valueType: 'currency',
        yScale: 'base',
      },
    ],
    tableRowsPerPage: 10,
    graphID: DASHBOARD_GRAPH_AUCTIONS,
    isDemandSourceUsingBidderType: false,
  },
  orderedFilterKeys: [
    'Publica Publishers',
    'Auction Requesters',
    'Channels',
    'Clusters',
    'Domains',
    'Regions',
    'Filtered Demand Sources',
    'Device Types',
  ],
  filters: {
    'Publica Publishers': {
      queryKey: 'publica_publisher_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      displayID: true,
      category: 'publisher',
    },
    'Auction Requesters': {
      queryKey: 'publica_auction_requesters',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'requester',
    },
    Channels: {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 20,
      displayID: true,
      category: 'supply',
    },
    Clusters: {
      queryKey: 'publica_clusters',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'cluster',
    },
    Domains: {
      queryKey: 'publica_domains',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'domain',
    },
    Regions: {
      queryKey: 'publica_regions',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'region',
    },
    'Filtered Demand Sources': {
      queryKey: 'publica_bidder_names',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'demand',
      isGroup: true,
    },
    'Device Types': {
      queryKey: 'publica_device_types',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      category: 'device',
    },
  } as ICustomAPIFilterConfig,
  cards: {
    groupings: {
      noDemandSelected: [
        'Revenue',
        'WinningBids',
        'Impressions',
        'CPM',
        'EndpointRequests',
        'RenderRate',
        'CPMPerSecond',
        'EndpointRequestsECPM',
        'Auctions',
        'RenderedPodFillRate',
        'PodFillRate',
        'PodClearRate',
        'PaidPodClearRate',
        'BidRequests',
        'BidResponses',
        'BidResponseRate',
        'BidTimeoutRate',
        'BidWinRate',
        'FillRate',
        'BidSaverTrueLift',
        'BidSaverImpressions',
        'BidSaverWinningBids',
      ],
      demandSelected: [
        'Revenue',
        'WinningBids',
        'Impressions',
        'CPM',
        'BidResponses',
        'RenderRate',
        'CPMPerSecond',
        'BidRequests',
        'BidResponseRate',
        'BidTimeoutRate',
        'BidWinRate',
        'BidSaverTrueLift',
        'BidSaverImpressions',
        'BidSaverWinningBids',
      ],
    },
    metrics: {
      Revenue: {
        key: 'Revenue',
        label: 'Revenue',
        responseKey: 'Revenue',
        trendKey: 'RevenueTrend',
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      Impressions: {
        key: 'Impressions',
        label: 'Impressions',
        responseKey: 'Impressions',
        trendKey: 'ImpressionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      CPM: {
        key: 'CPM',
        label: 'CPM',
        responseKey: 'CPM',
        trendKey: 'CPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      CPMPerSecond: {
        key: 'CPMPerSecond',
        label: 'CPM Per Second',
        responseKey: 'CPMPerSecond',
        trendKey: 'CPMPerSecondTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      RenderRate: {
        key: 'RenderRate',
        label: 'Render Rate',
        responseKey: 'RenderRate',
        trendKey: 'RenderRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      EndpointRequests: {
        key: 'EndpointRequests',
        label: 'Endpoint Requests',
        responseKey: 'EndpointRequests',
        trendKey: 'EndpointRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      EndpointRequestsECPM: {
        key: 'EndpointRequestsECPM',
        label: 'Endpoint Requests eCPM',
        responseKey: 'EndpointRequestsECPM',
        trendKey: 'EndpointRequestsECPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
        yScale: 'small',
      },
      Auctions: {
        key: 'Auctions',
        label: 'Auctions',
        responseKey: 'Auctions',
        trendKey: 'AuctionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      RenderedPodFillRate: {
        key: 'RenderedPodFillRate',
        label: 'Rendered Pod Fill Rate',
        responseKey: 'RenderedPodFillRate',
        trendKey: 'RenderedPodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PodFillRate: {
        key: 'PodFillRate',
        label: 'Pod Fill Rate',
        responseKey: 'PodFillRate',
        trendKey: 'PodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PodClearRate: {
        key: 'PodClearRate',
        label: 'Pod Clear Rate',
        responseKey: 'PodClearRate',
        trendKey: 'PodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      PaidPodClearRate: {
        key: 'PaidPodClearRate',
        label: 'Paid Pod Clear Rate',
        responseKey: 'PaidPodClearRate',
        trendKey: 'PaidPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      FillRate: {
        key: 'FillRate',
        label: 'Fill Rate',
        responseKey: 'FillRate',
        trendKey: 'FillRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidRequests: {
        key: 'BidRequests',
        label: 'Bid Requests',
        responseKey: 'BidRequests',
        trendKey: 'BidRequestsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidResponses: {
        key: 'BidResponses',
        label: 'Bid Responses',
        responseKey: 'BidResponses',
        trendKey: 'BidResponsesTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      WinningBids: {
        key: 'WinningBids',
        label: 'Winning Bids',
        responseKey: 'WinningBids',
        trendKey: 'WinningBidsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidWinRate: {
        key: 'BidWinRate',
        label: 'Bid Win Rate',
        responseKey: 'BidWinRate',
        trendKey: 'BidWinRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidResponseRate: {
        key: 'BidResponseRate',
        label: 'Bid Response Rate',
        responseKey: 'BidResponseRate',
        trendKey: 'BidResponseRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidTimeoutRate: {
        isInverseTrend: true,
        key: 'BidTimeoutRate',
        label: 'Bid Timeout Rate',
        responseKey: 'BidTimeoutRate',
        trendKey: 'BidTimeoutRateTrend',
        valueType: 'percent',
        chartType: 'line',
        yScale: 'small',
      },
      BidSaverTrueLift: {
        key: 'BidSaverTrueLift',
        label: 'Publica Vault True Lift',
        responseKey: 'BidSaverTrueLift',
        trendKey: 'BidSaverTrueLiftTrend',
        valueType: 'currency',
        chartType: 'bar',
        yScale: 'base',
      },
      BidSaverImpressions: {
        key: 'BidSaverImpressions',
        label: 'Publica Vault Impressions',
        responseKey: 'BidSaverImpressions',
        trendKey: 'BidSaverImpressionsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
      BidSaverWinningBids: {
        key: 'BidSaverWinningBids',
        label: 'Publica Vault Winning Bids',
        responseKey: 'BidSaverWinningBids',
        trendKey: 'BidSaverWinningBidsTrend',
        valueType: 'integer',
        chartType: 'line',
        yScale: 'large',
      },
    } as Record<IDashboardHomeCardMetricKey, IDashboardCard>,
  },
  subCharts: [
    {
      title: 'Key Latencies (ms)',
      metrics: { ...keyLatencyMetrics, ...clientKeyLatencyMetrics },
      orderedMetricKeys: [
        'EndpointLatency',
        'AuctionLatency',
        'BidderLatency',
        'CreationLatency',
        'WaitLatency',
      ],
    },
    {
      title: 'Bid Errors',
      metrics: { ...bidErrorsMetrics, ...clientBidErrorsMetrics },
      orderedMetricKeys: ['BidErrors', 'BidTimeouts', 'BidSchedulingIssues'],
    },
  ],
};
